import type {Instant, LocalizedMessage, UUID} from './common'
import type {EntityLink} from './entity-link'
import {BadgeVariant} from '@/components/ui/badge'
import {EnumValue} from '@/components/shared/EnumValue'
import type {useI18n} from '@/i18n'

type I18n = ReturnType<typeof useI18n>

/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */

export const TargetEntityType = {
    PARTY: 'PARTY',
    IDENTITY: 'IDENTITY',
    RESOURCE: 'RESOURCE',
    FUNCTION_GROUP: 'FUNCTION_GROUP',
} as const
export type TargetEntityType = typeof TargetEntityType[keyof typeof TargetEntityType]

export const AttributeMappingTransform = {
    LOOKUP_ORG_UNIT: 'LOOKUP_ORG_UNIT',
    RESOLVE_MANAGER: 'RESOLVE_MANAGER',
    TO_DATE: 'TO_DATE',
    TO_UPPERCASE: 'TO_UPPERCASE',
    TO_LOWERCASE: 'TO_LOWERCASE',
    TO_AD_GENERALIZED_TIME: 'TO_AD_GENERALIZED_TIME',
    TO_WINDOWS_FILETIME: 'TO_WINDOWS_FILETIME',
    PREFIX: 'PREFIX',
    SUFFIX: 'SUFFIX',
    REPLACE: 'REPLACE',
    SPLIT: 'SPLIT',
    JOIN: 'JOIN',
    REGEX_EXTRACT: 'REGEX_EXTRACT',
    CONDITIONAL: 'CONDITIONAL',
} as const
export type AttributeMappingTransform = typeof AttributeMappingTransform[keyof typeof AttributeMappingTransform]

/**
 * Rich enum for {@link AttributeMappingTransform} carrying i18n labels.
 */
export class AttributeMappingTransformBadge extends EnumValue {
    static readonly LOOKUP_ORG_UNIT = new AttributeMappingTransformBadge(
        AttributeMappingTransform.LOOKUP_ORG_UNIT,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundAttributeMappings.transformLookupOrgUnit as string,
    )
    static readonly RESOLVE_MANAGER = new AttributeMappingTransformBadge(
        AttributeMappingTransform.RESOLVE_MANAGER,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundAttributeMappings.transformResolveManager as string,
    )
    static readonly TO_DATE = new AttributeMappingTransformBadge(
        AttributeMappingTransform.TO_DATE,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundAttributeMappings.transformToDate as string,
    )
    static readonly TO_UPPERCASE = new AttributeMappingTransformBadge(
        AttributeMappingTransform.TO_UPPERCASE,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundAttributeMappings.transformToUppercase as string,
    )
    static readonly TO_LOWERCASE = new AttributeMappingTransformBadge(
        AttributeMappingTransform.TO_LOWERCASE,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundAttributeMappings.transformToLowercase as string,
    )
    static readonly TO_AD_GENERALIZED_TIME = new AttributeMappingTransformBadge(
        AttributeMappingTransform.TO_AD_GENERALIZED_TIME,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundAttributeMappings.transformToAdGeneralizedTime as string,
    )
    static readonly TO_WINDOWS_FILETIME = new AttributeMappingTransformBadge(
        AttributeMappingTransform.TO_WINDOWS_FILETIME,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundAttributeMappings.transformToWindowsFiletime as string,
    )
    static readonly PREFIX = new AttributeMappingTransformBadge(
        AttributeMappingTransform.PREFIX,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundAttributeMappings.transformPrefix as string,
    )
    static readonly SUFFIX = new AttributeMappingTransformBadge(
        AttributeMappingTransform.SUFFIX,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundAttributeMappings.transformSuffix as string,
    )
    static readonly REPLACE = new AttributeMappingTransformBadge(
        AttributeMappingTransform.REPLACE,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundAttributeMappings.transformReplace as string,
    )
    static readonly SPLIT = new AttributeMappingTransformBadge(
        AttributeMappingTransform.SPLIT,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundAttributeMappings.transformSplit as string,
    )
    static readonly JOIN = new AttributeMappingTransformBadge(
        AttributeMappingTransform.JOIN,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundAttributeMappings.transformJoin as string,
    )
    static readonly REGEX_EXTRACT = new AttributeMappingTransformBadge(
        AttributeMappingTransform.REGEX_EXTRACT,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundAttributeMappings.transformRegexExtract as string,
    )
    static readonly CONDITIONAL = new AttributeMappingTransformBadge(
        AttributeMappingTransform.CONDITIONAL,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundAttributeMappings.transformConditional as string,
    )

    static readonly values: ReadonlyArray<AttributeMappingTransformBadge> = [
        AttributeMappingTransformBadge.LOOKUP_ORG_UNIT,
        AttributeMappingTransformBadge.RESOLVE_MANAGER,
        AttributeMappingTransformBadge.TO_DATE,
        AttributeMappingTransformBadge.TO_UPPERCASE,
        AttributeMappingTransformBadge.TO_LOWERCASE,
        AttributeMappingTransformBadge.TO_AD_GENERALIZED_TIME,
        AttributeMappingTransformBadge.TO_WINDOWS_FILETIME,
        AttributeMappingTransformBadge.PREFIX,
        AttributeMappingTransformBadge.SUFFIX,
        AttributeMappingTransformBadge.REPLACE,
        AttributeMappingTransformBadge.SPLIT,
        AttributeMappingTransformBadge.JOIN,
        AttributeMappingTransformBadge.REGEX_EXTRACT,
        AttributeMappingTransformBadge.CONDITIONAL,
    ]

    static of(code: AttributeMappingTransform | null | undefined): AttributeMappingTransformBadge | undefined {
        return AttributeMappingTransformBadge.values.find((v) => v.code === code)
    }
}

export const InboundMappingType = {
    DIRECT: 'DIRECT',
    COMPUTED: 'COMPUTED',
    STATIC: 'STATIC',
} as const
export type InboundMappingType = typeof InboundMappingType[keyof typeof InboundMappingType]

/**
 * Rich enum for {@link InboundMappingType} carrying i18n labels.
 */
export class InboundMappingTypeBadge extends EnumValue {
    static readonly DIRECT = new InboundMappingTypeBadge(
        InboundMappingType.DIRECT,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundAttributeMappings.typeDirect as string,
    )
    static readonly COMPUTED = new InboundMappingTypeBadge(
        InboundMappingType.COMPUTED,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundAttributeMappings.typeComputed as string,
    )
    static readonly STATIC = new InboundMappingTypeBadge(
        InboundMappingType.STATIC,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundAttributeMappings.typeStatic as string,
    )

    static readonly values: ReadonlyArray<InboundMappingTypeBadge> = [
        InboundMappingTypeBadge.DIRECT,
        InboundMappingTypeBadge.COMPUTED,
        InboundMappingTypeBadge.STATIC,
    ]

    static of(code: InboundMappingType | null | undefined): InboundMappingTypeBadge | undefined {
        return InboundMappingTypeBadge.values.find((v) => v.code === code)
    }
}

export const InboundConnectorType = {
    REST: 'REST',
    SCIM_2_0: 'SCIM_2_0',
    SCRIPT: 'SCRIPT',
    SAP_HCM: 'SAP_HCM',
    SAP_SF_EC: 'SAP_SF_EC',
} as const
export type InboundConnectorType = typeof InboundConnectorType[keyof typeof InboundConnectorType]

/**
 * Rich enum for {@link InboundConnectorType} carrying i18n labels.
 */
export class InboundConnectorTypeBadge extends EnumValue {
    static readonly REST = new InboundConnectorTypeBadge(
        InboundConnectorType.REST,
        BadgeVariant.OUTLINE,
        (i18n: I18n) => i18n.inboundConnectors.typeRest as string,
    )
    static readonly SCIM_2_0 = new InboundConnectorTypeBadge(
        InboundConnectorType.SCIM_2_0,
        BadgeVariant.OUTLINE,
        (i18n: I18n) => i18n.inboundConnectors.typeScim as string,
    )
    static readonly SCRIPT = new InboundConnectorTypeBadge(
        InboundConnectorType.SCRIPT,
        BadgeVariant.OUTLINE,
        (i18n: I18n) => i18n.inboundConnectors.typeScript as string,
    )
    static readonly SAP_HCM = new InboundConnectorTypeBadge(
        InboundConnectorType.SAP_HCM,
        BadgeVariant.OUTLINE,
        (i18n: I18n) => i18n.inboundConnectors.typeSapHcm as string,
    )
    static readonly SAP_SF_EC = new InboundConnectorTypeBadge(
        InboundConnectorType.SAP_SF_EC,
        BadgeVariant.OUTLINE,
        (i18n: I18n) => i18n.inboundConnectors.typeSapSfEc as string,
    )

    static readonly values: ReadonlyArray<InboundConnectorTypeBadge> = [
        InboundConnectorTypeBadge.SCIM_2_0,
        InboundConnectorTypeBadge.REST,
        InboundConnectorTypeBadge.SCRIPT,
        InboundConnectorTypeBadge.SAP_HCM,
        InboundConnectorTypeBadge.SAP_SF_EC,
    ]

    static of(code: InboundConnectorType | null | undefined): InboundConnectorTypeBadge | undefined {
        return InboundConnectorTypeBadge.values.find((v) => v.code === code)
    }
}

export const SyncType = {
    FULL: 'FULL',
    DELTA: 'DELTA',
    PREVIEW: 'PREVIEW',
} as const
export type SyncType = typeof SyncType[keyof typeof SyncType]

export const SyncRunStatus = {
    RUNNING: 'RUNNING',
    COMPLETED: 'COMPLETED',
    COMPLETED_WITH_ERRORS: 'COMPLETED_WITH_ERRORS',
    FAILED: 'FAILED',
    CANCELLED: 'CANCELLED',
    PAUSED: 'PAUSED',
} as const
export type SyncRunStatus = typeof SyncRunStatus[keyof typeof SyncRunStatus]

export const InboundAuthType = {
    BEARER_TOKEN: 'BEARER_TOKEN',
    OAUTH2_CLIENT: 'OAUTH2_CLIENT',
    OAUTH2_SAML_BEARER: 'OAUTH2_SAML_BEARER',
    BASIC_AUTH: 'BASIC_AUTH',
    API_KEY: 'API_KEY',
    MUTUAL_TLS: 'MUTUAL_TLS',
    AWS_SIGV4: 'AWS_SIGV4',
    SIGNED_REQUEST: 'SIGNED_REQUEST',
    NTLM_KERBEROS: 'NTLM_KERBEROS',
    GOOGLE_SA_JWT: 'GOOGLE_SA_JWT',
    NONE: 'NONE',
} as const
export type InboundAuthType = typeof InboundAuthType[keyof typeof InboundAuthType]

/**
 * Whitelisted signing algorithms for the {@link InboundAuthType.SIGNED_REQUEST} mode.
 * Hardcoded — mirrors the backend whitelist enforced at three layers
 * (frontend Select, API @Pattern, DB CHECK). 'none' is intentionally NOT a member.
 */
export const SigningAlgorithm = {
    HS256: 'HS256',
    RS256: 'RS256',
    HMAC_SHA256: 'HMAC_SHA256',
} as const
export type SigningAlgorithm = typeof SigningAlgorithm[keyof typeof SigningAlgorithm]

export const SIGNING_ALGORITHM_OPTIONS: ReadonlyArray<SigningAlgorithm> = [
    SigningAlgorithm.HS256,
    SigningAlgorithm.RS256,
    SigningAlgorithm.HMAC_SHA256,
]

export const ResponseFormat = {
    JSON: 'JSON',
    XML: 'XML',
    CSV: 'CSV',
    TEXT: 'TEXT',
} as const
export type ResponseFormat = typeof ResponseFormat[keyof typeof ResponseFormat]

export const PaginationStyle = {
    OFFSET: 'OFFSET',
    PAGE: 'PAGE',
    CURSOR: 'CURSOR',
    NONE: 'NONE',
} as const
export type PaginationStyle = typeof PaginationStyle[keyof typeof PaginationStyle]

/**
 * Rich enum for {@link PaginationStyle} carrying i18n labels — replaces
 * the previous behaviour of rendering the raw enum code as label.
 */
export class PaginationStyleBadge extends EnumValue {
    static readonly OFFSET = new PaginationStyleBadge(
        PaginationStyle.OFFSET,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundConnectors.paginationOffset as string,
    )
    static readonly PAGE = new PaginationStyleBadge(
        PaginationStyle.PAGE,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundConnectors.paginationPage as string,
    )
    static readonly CURSOR = new PaginationStyleBadge(
        PaginationStyle.CURSOR,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.inboundConnectors.paginationCursor as string,
    )
    static readonly NONE = new PaginationStyleBadge(
        PaginationStyle.NONE,
        BadgeVariant.OUTLINE,
        (i18n: I18n) => i18n.inboundConnectors.paginationNone as string,
    )

    static readonly values: ReadonlyArray<PaginationStyleBadge> = [
        PaginationStyleBadge.OFFSET,
        PaginationStyleBadge.PAGE,
        PaginationStyleBadge.CURSOR,
        PaginationStyleBadge.NONE,
    ]

    static of(code: PaginationStyle | null | undefined): PaginationStyleBadge | undefined {
        return PaginationStyleBadge.values.find((v) => v.code === code)
    }
}

export const EnforcementLevel = {
    STRICT: 'STRICT',
    WARN: 'WARN',
    OFF: 'OFF',
} as const
export type EnforcementLevel = typeof EnforcementLevel[keyof typeof EnforcementLevel]

export const RehirePolicy = {
    HUMAN_REVIEW: 'HUMAN_REVIEW',
    AUTO_RESURRECT: 'AUTO_RESURRECT',
    IGNORE: 'IGNORE',
} as const
export type RehirePolicy = typeof RehirePolicy[keyof typeof RehirePolicy]

export const InboundConnectorListResponseFields = {
    id: 'id',
    displayName: 'displayName',
    targetSystem: 'targetSystem',
    connectorType: 'connectorType',
    enabled: 'enabled',
    lastSyncAt: 'lastSyncAt',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

/* eslint-enable i18next/no-literal-string */

export interface InboundConnectorList {
    id: UUID
    displayName: string
    targetSystem: EntityLink
    connectorType: string
    enabled: boolean
    lastSyncAt: Instant | null
    version: number
    createdAt: Instant
    updatedAt: Instant
}

export interface InboundConnector {
    id: UUID
    displayName: string
    targetSystem: EntityLink
    targetSystemAuthoritativeForIdentities: boolean
    connectorType: string
    enabled: boolean
    cronExpression: string
    description: string | null
    settings: InboundConnectorSettings
    lastSyncAt: Instant | null
    version: number
    createdAt: Instant
    updatedAt: Instant
    allowedOperations: string[]
    scriptConfig: ScriptInboundConfig | null
}

export interface InboundConnectorSettings {
    entityOperations: Record<string, string[]>
    safeguards: SafeguardSettings
    validation: ValidationSettings
    batchSize: number
    rehirePolicy: RehirePolicy
    partyTypeCode: string
    identityTypeCode: string
    createParty: boolean
    createIdentity: boolean
    createAccount: boolean
    externalIdField: string
    lifecycleSourceField: string
    lifecycleEnabledValue: string
}

export interface SafeguardSettings {
    enabled: boolean
    maxDeletePercent: number
    maxDeleteAbsolute: number
    maxUpdatePercent: number
    maxCreatePercent: number
    emptySourceProtection: boolean
}

export interface ValidationSettings {
    attributeValidation: string
    relationshipSchemaEnforcement: string
    identifierUniqueness: string
}

export interface InboundConnectorCreateRequest {
    displayName: string
    targetSystemId: UUID
    connectorType: string
    cronExpression: string | null
    description: string | null
    enabled: boolean | null
}

export interface InboundConnectorUpdateRequest {
    displayName: string
    description?: string
    cronExpression?: string
    version: number
}

export interface RestInboundConfig {
    id: UUID
    baseUrl: string
    authType: string
    tokenUrl: string | null
    clientId: string | null
    scope: string | null
    username: string | null
    apiKeyHeader: string | null
    awsRegion: string | null
    awsService: string | null
    signingAlgorithm: string | null
    signingHeaderTemplate: string | null
    kerberosPrincipal: string | null
    kerberosRealm: string | null
    googleSaEmail: string | null
    googleSaKeySecretRef: string | null
    googleDelegatedUser: string | null
    googleTokenScope: string | null
    samlAssertionTemplate: string | null
    samlIssuer: string | null
    samlAudience: string | null
    samlSubject: string | null
    itemsEndpoint: string
    itemsJsonPath: string
    responseFormat: ResponseFormat
    totalCountJsonPath: string | null
    paginationStyle: string
    pageSize: number
    connectTimeoutMs: number
    readTimeoutMs: number
    maxRetries: number
    attributeMappings: InboundAttributeMapping[] | null
    queryFilter: string | null
    responseFilters: ResponseFilterEntry[]
    version: number
}

export interface ScimInboundConfig {
    id: UUID
    scimFilter: string | null
    enterpriseExtEnabled: boolean
    version: number
}

export interface ConnectionTest {
    success: boolean
    message: LocalizedMessage | null
    responseTimeMs: number | null
}

/**
 * Composite reference to a partitioned `error_logs` row. The `error_logs` table is
 * range-partitioned by `created_at`, so a foreign-key reference to an entry must carry
 * both `errorLogId` and `errorLogCreatedAt` (matches the table's composite primary key).
 * Mirrors the Java {@code com.irian.iam.job.api.ErrorLogRef} type.
 */
export interface ErrorLogRef {
    errorLogId: UUID
    errorLogCreatedAt: Instant
}

export interface InboundSyncRun {
    syncRunId: UUID
    inboundConnectorId: UUID
    syncType: string
    status: string
    triggeredBy: string
    triggeredByUser: string | null
    startedAt: Instant
    completedAt: Instant | null
    recordsRead: number
    recordsCreated: number
    recordsUpdated: number
    recordsDeleted: number
    recordsSkipped: number
    recordsErrored: number
    errorMessage: string | null
    createdAt: Instant
    /**
     * Composite refs to error_logs rows recording per-record failures from this run.
     * Populated by the run-detail endpoint only — the paginated list endpoint always
     * returns an empty list to avoid an N+1 query across the page.
     */
    errorLogIds: ErrorLogRef[]
}

/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */
export const SyncRecordAction = {
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
    DELETE: 'DELETE',
    SKIP: 'SKIP',
    ERROR: 'ERROR',
} as const
export type SyncRecordAction = typeof SyncRecordAction[keyof typeof SyncRecordAction]
/* eslint-enable i18next/no-literal-string */

export interface InboundSyncRecord {
    id: UUID
    syncRunId: UUID
    inboundConnectorId: UUID
    externalId: string
    entityType: string
    action: string | null
    correlationKey: string | null
    correlatedPartyId: UUID | null
    correlatedIdentityId: UUID | null
    errorMessage: string | null
    processedAt: Instant | null
    createdAt: Instant
    rawData?: Record<string, unknown> | null
    mappedData?: Record<string, unknown> | null
}

export interface SyncTriggerResponse {
    scheduled: boolean
}

export type PreviewResponse = Preview
export interface Preview {
    success: boolean
    statusCode: number
    contentType: string | null
    body: string | null
    errorMessage: string | null
}

export interface SyncStatus {
    running: boolean
    currentRunId: UUID | null
    lastRunStatus: string | null
    lastRunAt: Instant | null
}

export interface InboundSyncRunPage {
    content: InboundSyncRun[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

export interface InboundSyncRecordPage {
    content: InboundSyncRecord[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

export interface InboundConnectorPage {
    content: InboundConnectorList[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

export interface ResponseFilterEntry {
    fieldName: string
    operator: string
    expectedValue: string
}

export interface RestInboundConfigUpdateRequest {
    baseUrl: string
    authType: string
    tokenSecretRef?: string | null
    tokenUrl?: string | null
    clientId?: string | null
    clientSecretRef?: string | null
    scope?: string | null
    username?: string | null
    passwordSecretRef?: string | null
    apiKeyHeader?: string | null
    apiKeySecretRef?: string | null
    clientCertSecretRef?: string | null
    clientKeySecretRef?: string | null
    truststoreSecretRef?: string | null
    awsAccessKeySecretRef?: string | null
    awsSecretKeySecretRef?: string | null
    awsRegion?: string | null
    awsService?: string | null
    signingAlgorithm?: string | null
    signingKeySecretRef?: string | null
    signingHeaderTemplate?: string | null
    kerberosPrincipal?: string | null
    kerberosKeytabSecretRef?: string | null
    kerberosRealm?: string | null
    googleSaEmail?: string | null
    googleSaKeySecretRef?: string | null
    googleDelegatedUser?: string | null
    googleTokenScope?: string | null
    samlAssertionTemplate?: string | null
    samlIssuer?: string | null
    samlAudience?: string | null
    samlSubject?: string | null
    samlSigningKeyRef?: string | null
    samlSigningCertificateRef?: string | null
    itemsEndpoint: string
    itemsJsonPath: string | null
    responseFormat: ResponseFormat | null
    totalCountJsonPath?: string | null
    paginationStyle: string
    pageSize: number
    connectTimeoutMs: number
    readTimeoutMs: number
    maxRetries: number
    queryFilter?: string | null
    attributeMappings?: InboundAttributeMapping[] | null
    responseFilters?: ResponseFilterEntry[] | null
    version: number
}

export interface ScimInboundConfigUpdateRequest {
    scimFilter?: string | null
    enterpriseExtEnabled: boolean
    version: number
}

export interface InboundConnectorSettingsUpdateRequest {
    safeguards: SafeguardUpdateRequest
    validation: ValidationUpdateRequest
    batchSize: number
    entityOperations?: Record<string, string[]> | null
    rehirePolicy?: RehirePolicy | null
    partyTypeCode?: string | null
    identityTypeCode?: string | null
    createParty?: boolean | null
    createIdentity?: boolean | null
    createAccount?: boolean | null
    externalIdField?: string | null
    lifecycleSourceField?: string | null
    lifecycleEnabledValue?: string | null
    version: number
}

export interface SafeguardUpdateRequest {
    enabled: boolean
    maxDeletePercent: number
    maxDeleteAbsolute: number
    maxUpdatePercent: number
    maxCreatePercent: number
    emptySourceProtection: boolean
}

export interface ValidationUpdateRequest {
    attributeValidation: string
    relationshipSchemaEnforcement: string
    identifierUniqueness: string
}

export interface InboundAttributeMapping {
    source: string
    target: string
    targetEntityType: string
    identifierType: string | null
    transform: string | null
    type: string | null
    required: boolean
    transformArgs: Record<string, unknown> | null
}

/**
 * Wrapper for the GET/PUT /attribute-mappings endpoint.
 * Matches Java InboundAttributeMappingListResponse.
 */
export interface InboundAttributeMappingList {
    attributeMappings: InboundAttributeMapping[] | null
}

export type InboundAttributeMappingResponse = InboundAttributeMappingList;

export interface InboundAttributeMappingRequest {
    attributeMappings: InboundAttributeMapping[] | null
}

/* eslint-disable i18next/no-literal-string -- enum values are API keys, not user-facing strings */
export const InboundPhase = {
    TEST_CONNECTION: 'TEST_CONNECTION',
    FULL_SYNC: 'FULL_SYNC',
} as const
export type InboundPhase = typeof InboundPhase[keyof typeof InboundPhase]

/** Mirrors Java enum {@code InboundOperationMethod} — enum values are API keys, not user-facing strings. */
export const InboundOperationMethod = {
    TEST_CONNECTION: 'TEST_CONNECTION',
    FULL_SYNC: 'FULL_SYNC',
} as const
export type InboundOperationMethod = typeof InboundOperationMethod[keyof typeof InboundOperationMethod]
/* eslint-enable i18next/no-literal-string */

export interface ScriptInboundConfig {
    id: UUID
    inboundConnectorId: UUID
    baseUrl: string | null
    authType: string
    tokenUrl: string | null
    clientId: string | null
    scope: string | null
    username: string | null
    apiKeyHeader: string | null
    script: string
    // 571.06: 4 boolean gates replace the legacy enabledOperations Map.
    createEnabled: boolean
    updateEnabled: boolean
    deleteEnabled: boolean
    queryEnabled: boolean
    connectTimeoutMs: number
    readTimeoutMs: number
    // Issue 318 / 571.06: opt-in attribute mappings (now backed by a child table).
    attributeMappings: InboundAttributeMapping[] | null
    version: number
    createdAt: Instant
    updatedAt: Instant
}

export interface ScriptInboundConfigCreateRequest {
    baseUrl?: string | null
    authType?: string | null
    tokenSecretRef?: string | null
    tokenUrl?: string | null
    clientId?: string | null
    clientSecretRef?: string | null
    scope?: string | null
    username?: string | null
    passwordSecretRef?: string | null
    apiKeyHeader?: string | null
    apiKeySecretRef?: string | null
    script?: string | null
    createEnabled?: boolean | null
    updateEnabled?: boolean | null
    deleteEnabled?: boolean | null
    queryEnabled?: boolean | null
    connectTimeoutMs?: number | null
    readTimeoutMs?: number | null
    attributeMappings?: InboundAttributeMapping[] | null
}

export interface ScriptInboundConfigUpdateRequest extends ScriptInboundConfigCreateRequest {
    version: number
}

/**
 * Response from the inbound script validate endpoint.
 * Matches Java ScriptValidationResult.
 */
export interface InboundScriptValidationError {
    line: number
    column: number
    message: string
}

export interface InboundScriptValidationResult {
    valid: boolean
    errors: InboundScriptValidationError[]
    supportedOperations: string[]
    unsupportedOperations: string[]
    warnings: string[]
}

// ====================================================================================
// SAP_HCM inbound config
// ====================================================================================

export interface SapHcmEnabledInfotype {
    infotype: string
    sequence: number
    targetEntityType: string
}

export interface SapHcmAttributeMapping {
    sequence: number
    infotype: string
    source: string
    target: string
    targetEntityType: string
    identifierType: string | null
    transformKind: string | null
    transformArgs: string | null
    mappingType: string | null
    required: boolean
}

export interface SapHcmInboundConfig {
    id: UUID
    baseUrl: string
    employeeKeyField: string
    validityStartField: string
    validityEndField: string
    validityResolutionStrategy: string
    authType: string
    tokenUrl: string | null
    clientId: string | null
    scope: string | null
    username: string | null
    pageSize: number
    connectTimeoutMs: number
    readTimeoutMs: number
    maxRetries: number
    terminationAttribute: string
    terminationValuesCsv: string
    enabledInfotypes: SapHcmEnabledInfotype[]
    attributeMappings: SapHcmAttributeMapping[]
    version: number
}

// ====================================================================================
// SAP_SF_EC inbound config
// ====================================================================================

export interface SapSfEcAttributeMapping {
    sequence: number
    source: string
    target: string
    targetEntityType: string
    identifierType: string | null
    transformKind: string | null
    transformArgs: string | null
    mappingType: string | null
    required: boolean
}

export interface SapSfEcInboundConfig {
    id: UUID
    baseUrl: string
    entitySet: string
    selectFieldsCsv: string | null
    expandFieldsCsv: string | null
    datetimeFormat: string
    authType: string
    tokenUrl: string | null
    clientId: string | null
    scope: string | null
    samlAssertionTemplate: string | null
    samlIssuer: string | null
    samlAudience: string | null
    samlSubject: string | null
    pageSize: number
    connectTimeoutMs: number
    readTimeoutMs: number
    maxRetries: number
    attributeMappings: SapSfEcAttributeMapping[]
    version: number
}

export interface SapHcmInboundConfigUpdateRequest {
    baseUrl: string
    employeeKeyField: string
    validityStartField: string
    validityEndField: string
    validityResolutionStrategy: string
    authType: string
    tokenSecretRef?: string | null
    tokenUrl?: string | null
    clientId?: string | null
    clientSecretRef?: string | null
    scope?: string | null
    username?: string | null
    passwordSecretRef?: string | null
    pageSize: number
    connectTimeoutMs: number
    readTimeoutMs: number
    maxRetries: number
    terminationAttribute: string
    terminationValuesCsv: string
    enabledInfotypes?: SapHcmEnabledInfotypeEntry[] | null
    attributeMappings?: SapHcmAttributeMappingEntry[] | null
    version: number
}

export interface SapHcmEnabledInfotypeEntry {
    infotype: string
    sequence: number
    targetEntityType: string
}

export interface SapHcmAttributeMappingEntry {
    sequence: number
    infotype: string
    source: string
    target: string
    targetEntityType: string
    identifierType?: string | null
    transformKind?: string | null
    transformArgs?: string | null
    mappingType?: string | null
    required: boolean
}

export interface SapSfEcInboundConfigUpdateRequest {
    baseUrl: string
    entitySet: string
    selectFieldsCsv?: string | null
    expandFieldsCsv?: string | null
    datetimeFormat: string
    authType: string
    tokenUrl?: string | null
    clientId?: string | null
    clientSecretRef?: string | null
    scope?: string | null
    samlAssertionTemplate?: string | null
    samlIssuer?: string | null
    samlAudience?: string | null
    samlSubject?: string | null
    samlSigningKeyRef?: string | null
    samlSigningCertificateRef?: string | null
    pageSize: number
    connectTimeoutMs: number
    readTimeoutMs: number
    maxRetries: number
    attributeMappings?: SapSfEcAttributeMappingEntry[] | null
    version: number
}

export interface SapSfEcAttributeMappingEntry {
    sequence: number
    source: string
    target: string
    targetEntityType: string
    identifierType?: string | null
    transformKind?: string | null
    transformArgs?: string | null
    mappingType?: string | null
    required: boolean
}

export interface SapHcmInboundConfigCreateRequest {
    baseUrl?: string | null
    employeeKeyField?: string | null
    validityStartField?: string | null
    validityEndField?: string | null
    validityResolutionStrategy?: string | null
    authType?: InboundAuthType | null
    tokenSecretRef?: string | null
    tokenUrl?: string | null
    clientId?: string | null
    clientSecretRef?: string | null
    scope?: string | null
    username?: string | null
    passwordSecretRef?: string | null
    pageSize?: number | null
    connectTimeoutMs?: number | null
    readTimeoutMs?: number | null
    maxRetries?: number | null
    terminationAttribute?: string | null
    terminationValuesCsv?: string | null
}

export interface SapSfEcInboundConfigCreateRequest {
    baseUrl?: string | null
    entitySet?: string | null
    selectFieldsCsv?: string | null
    expandFieldsCsv?: string | null
    datetimeFormat?: string | null
    authType?: string | null
    tokenUrl?: string | null
    clientId?: string | null
    clientSecretRef?: string | null
    scope?: string | null
    samlAssertionTemplate?: string | null
    samlIssuer?: string | null
    samlAudience?: string | null
    samlSubject?: string | null
    samlSigningKeyRef?: string | null
    samlSigningCertificateRef?: string | null
    pageSize?: number | null
    connectTimeoutMs?: number | null
    readTimeoutMs?: number | null
    maxRetries?: number | null
}
